/**
 * Tabla genérica de comparación por cuenta (Año vs Año / Mes vs Mes / Acumulado).
 * - Panel estilo Excel: thead sticky (top-0), 1ª columna sticky (left-0).
 * - Scroll container con altura máxima para que los encabezados y la columna
 *   de cuenta queden siempre visibles mientras el usuario navega la tabla.
 * - Filtro de texto en tiempo real sobre código y descripción de cuenta.
 */
import { useState } from 'react';
import { money, pctFmt, varColor } from './comparativosUtils';

export interface ComparisonRow {
  tipoCuenta: 'INGRESO' | 'GASTOS';
  cuentaCodigo: string;
  cuentaDescripcion: string;
  valor1: number;
  valor2: number;
  variacionAbsoluta: number;
  variacionPorcentaje: number | null;
}

interface Props {
  rows: ComparisonRow[];
  label1: string;
  label2: string;
  highlightTopN?: number;
}

export function ComparisonTable({ rows, label1, label2, highlightTopN = 5 }: Props) {
  const [filter, setFilter] = useState<'ALL' | 'INGRESO' | 'GASTOS'>('ALL');
  const [search, setSearch] = useState('');

  const byType = filter === 'ALL' ? rows : rows.filter((r) => r.tipoCuenta === filter);

  const q = search.trim().toLowerCase();
  const displayed = q
    ? byType.filter(
        (r) =>
          r.cuentaCodigo.toLowerCase().includes(q) ||
          r.cuentaDescripcion.toLowerCase().includes(q),
      )
    : byType;

  // Top N por variación absoluta (sobre el conjunto completo, no filtrado)
  const topKeys = new Set(
    [...rows]
      .sort((a, b) => Math.abs(b.variacionAbsoluta) - Math.abs(a.variacionAbsoluta))
      .slice(0, highlightTopN)
      .map((r) => `${r.tipoCuenta}::${r.cuentaCodigo}`),
  );

  return (
    <div className="space-y-2">
      {/* Controles: filtro por tipo + búsqueda de texto */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1.5 text-sm">
          {(['ALL', 'INGRESO', 'GASTOS'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1 font-medium transition-colors ${filter === f ? 'bg-brand-600 text-white' : 'bg-surface-2 text-ink-600 hover:bg-surface-3'}`}
            >
              {f === 'ALL' ? 'Todas' : f === 'INGRESO' ? 'Ingresos' : 'Gastos'}
            </button>
          ))}
        </div>

        {/* Búsqueda */}
        <div className="relative ml-auto">
          <svg className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
          </svg>
          <input
            type="text"
            placeholder="Buscar cuenta..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-lg border border-border bg-surface py-1.5 pl-8 pr-3 text-sm text-ink-700 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Tabla con scroll estilo Excel */}
      <div className="overflow-auto rounded-xl border border-border" style={{ maxHeight: 'calc(100vh - 340px)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-2 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
              {/* Celda esquina: sticky top-0 + left-0 → z-30 */}
              <th className="sticky left-0 top-0 z-30 whitespace-nowrap border-b border-r border-ink-100 bg-surface-2 px-4 py-3">
                Tipo
              </th>
              <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3">
                Cuenta
              </th>
              <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">
                {label1}
              </th>
              <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">
                {label2}
              </th>
              <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">
                Variación $
              </th>
              <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">
                Variación %
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {displayed.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-sm text-ink-400">
                  {search ? `Sin resultados para "${search}"` : 'Sin datos para los filtros seleccionados'}
                </td>
              </tr>
            )}
            {displayed.map((row) => {
              const key = `${row.tipoCuenta}::${row.cuentaCodigo}`;
              const isTop = topKeys.has(key);
              const colV = varColor(row.variacionAbsoluta, row.tipoCuenta);
              return (
                <tr
                  key={key}
                  className={`transition-colors hover:bg-surface-2 ${isTop ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''}`}
                >
                  {/* Columna Tipo: sticky left-0, fondo sólido para que no transparente */}
                  <td className="sticky left-0 z-10 whitespace-nowrap border-r border-ink-100 bg-inherit px-4 py-2.5">
                    <span
                      className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                        row.tipoCuenta === 'INGRESO'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                      }`}
                    >
                      {row.tipoCuenta === 'INGRESO' ? 'ING' : 'GTO'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <span className="font-mono text-xs text-ink-400">{row.cuentaCodigo}</span>
                    <span className="ml-2 text-ink-700">{row.cuentaDescripcion}</span>
                    {isTop && (
                      <span className="ml-2 text-[10px] font-bold text-amber-600">★ TOP</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-ink-600">
                    {money(row.valor1)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono font-semibold text-ink-900">
                    {money(row.valor2)}
                  </td>
                  <td className={`whitespace-nowrap px-4 py-2.5 text-right font-mono font-semibold ${colV}`}>
                    {row.variacionAbsoluta >= 0 ? '+' : ''}{money(row.variacionAbsoluta)}
                  </td>
                  <td className={`whitespace-nowrap px-4 py-2.5 text-right font-semibold ${colV}`}>
                    {pctFmt(row.variacionPorcentaje)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-ink-400">
        ★ TOP = entre las {highlightTopN} cuentas con mayor variación absoluta del período.
        {displayed.length !== rows.length && (
          <span className="ml-2">
            Mostrando {displayed.length} de {rows.length} cuentas.
          </span>
        )}
      </p>
    </div>
  );
}
